import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { DailyCommuteLocation } from "@/types/dailyCommuteLocation";
import type { CalculateSeatFareResponse, FuelType } from "@/types/fareCalculation";
import { toLocationFullAddress } from "@/utils/places/normalizeGooglePlace";
import { languageUtils } from "@/utils/languageUtils";
import {
  buildFareCalculationRequest,
  calculateSeatFare,
  prewarmFareEngine,
} from "@/services/fareEngineService";
import {
  fetchVehicleMakes,
  fetchVehicleModels,
} from "@/services/vehicleService";
import { resolveLocationFromCoordinates } from "@/services/googlePlacesRestService";
import { getBrowserLocation } from "@/utils/geoLocationUtils";
import {
  MIN_SEATS,
  DEFAULT_OFFERING_SEATS,
  maxOfferingSeats,
  MIN_AVAILABLE_SEATS,
  MAX_AVAILABLE_SEATS,
  DEFAULT_AVAILABLE_SEATS,
  DEFAULT_VEHICLE_MAKE,
  DEFAULT_VEHICLE_MODEL,
  findVehicleMakeByName,
  findVehicleModelByName,
} from "@/components/dailyCommute/commuteCalculatorConstants";

export interface UseCommuteCalculatorOptions {
  /** When false, skips location detection and API calls. */
  enabled?: boolean;
}


export function useCommuteCalculator(options: UseCommuteCalculatorOptions = {}) {
  const { enabled = true } = options;

  const [fromPlace, setFromPlace] = useState<DailyCommuteLocation | null>(null);
  const [toPlace, setToPlace] = useState<DailyCommuteLocation | null>(null);
  const [destinationKey, setDestinationKey] = useState(0);
  const [selectedMakeId, setSelectedMakeId] = useState("");
  const [selectedModelId, setSelectedModelId] = useState("");
  const [fuelType, setFuelType] = useState<FuelType>("Petrol");
  // Offering seats: how many seats the host shares (must not exceed available seats).
  const [seats, setSeats] = useState(DEFAULT_OFFERING_SEATS);
  // Available seats: total vehicle capacity. Defaults to 5; cleared (re-asked) when the
  // user changes the vehicle make.
  const [availableSeats, setAvailableSeats] = useState<number | null>(
    DEFAULT_AVAILABLE_SEATS
  );
  const [fareResult, setFareResult] = useState<CalculateSeatFareResponse | null>(null);
  const [selectedFare, setSelectedFare] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);

  const lang = languageUtils.getCurrentLanguage();
  const showFareResults = Boolean(fareResult);

  // Auto-detect the starting location only once, on first mount. Detecting again
  // whenever `fromPlace` becomes null would re-fill the field every time the user
  // clears it, which is disruptive.
  const hasAutoDetectedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    prewarmFareEngine();

    if (hasAutoDetectedRef.current) return;
    hasAutoDetectedRef.current = true;
    let cancelled = false;

    const detectCurrentLocation = async () => {
      setIsDetectingLocation(true);

      let coords: { lat: number; lng: number };
      try {
        coords = await getBrowserLocation({ timeout: 8000, maximumAge: 60_000 });
      } catch {
        // Permission denied or unavailable — user can search manually
        if (!cancelled) setIsDetectingLocation(false);
        return;
      }

      try {
        const detectedOrigin = await resolveLocationFromCoordinates(
          coords.lat,
          coords.lng
        );
        if (!cancelled) setFromPlace(detectedOrigin);
      } catch {
        // Reverse geocoding failed — user can search manually
      } finally {
        if (!cancelled) setIsDetectingLocation(false);
      }
    };

    void detectCurrentLocation();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const {
    data: makes = [],
    isLoading: makesLoading,
    isError: makesError,
  } = useQuery({
    queryKey: ["vehicle-makes", lang],
    queryFn: fetchVehicleMakes,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: models = [],
    isLoading: modelsLoading,
    isError: modelsError,
  } = useQuery({
    queryKey: ["vehicle-models", selectedMakeId, lang],
    queryFn: () => fetchVehicleModels(selectedMakeId),
    enabled: enabled && !!selectedMakeId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!enabled || makesLoading || makes.length === 0 || selectedMakeId) return;

    const defaultMake = findVehicleMakeByName(makes, DEFAULT_VEHICLE_MAKE);
    if (defaultMake) {
      setSelectedMakeId(defaultMake.maId);
    }
  }, [enabled, makesLoading, makes, selectedMakeId]);

  useEffect(() => {
    if (
      !enabled ||
      !selectedMakeId ||
      modelsLoading ||
      models.length === 0 ||
      selectedModelId
    ) {
      return;
    }

    const defaultMake = findVehicleMakeByName(makes, DEFAULT_VEHICLE_MAKE);
    if (!defaultMake || selectedMakeId !== defaultMake.maId) return;

    const defaultModel = findVehicleModelByName(models, DEFAULT_VEHICLE_MODEL);
    if (defaultModel) {
      setSelectedModelId(defaultModel.moId);
    }
  }, [
    enabled,
    selectedMakeId,
    modelsLoading,
    models,
    selectedModelId,
    makes,
  ]);

  // Keep offering seats within capacity (one seat is reserved for the driver).
  useEffect(() => {
    if (availableSeats == null) return;
    const cap = maxOfferingSeats(availableSeats);
    if (seats > cap) setSeats(cap);
  }, [availableSeats, seats]);

  const fareMutation = useMutation({
    mutationFn: calculateSeatFare,
    onSuccess: (response) => {
      setFareResult(response);
      setSelectedFare(response.fareCalculation.perSeatPricing.suggestedFare);
      setValidationError(null);
    },
    onError: (error: Error) => {
      setFareResult(null);
      setValidationError(error.message);
    },
  });

  const selectedMake = makes.find((make) => make.maId === selectedMakeId);
  const selectedModel = models.find((model) => model.moId === selectedModelId);

  const fromFullAddress = fromPlace ? toLocationFullAddress(fromPlace) : "";
  const toFullAddress = toPlace ? toLocationFullAddress(toPlace) : "";

  const clearValidationError = useCallback(() => {
    setValidationError(null);
  }, []);

  const clearFareResult = useCallback(() => {
    setFareResult(null);
    setSelectedFare(0);
    setShowExplanation(false);
  }, []);

  const resetForm = useCallback(() => {
    setFromPlace(null);
    setToPlace(null);
    setDestinationKey((key) => key + 1);
    setSelectedMakeId("");
    setSelectedModelId("");
    setFuelType("Petrol");
    setSeats(DEFAULT_OFFERING_SEATS);
    setAvailableSeats(DEFAULT_AVAILABLE_SEATS);
    setFareResult(null);
    setSelectedFare(0);
    setShowExplanation(false);
    setValidationError(null);
    setIsDetectingLocation(false);
    fareMutation.reset();
  }, [fareMutation]);

  const handleFromPlaceSelect = useCallback(
    (place: DailyCommuteLocation) => {
      clearFareResult();
      clearValidationError();
      setFromPlace((previous) => {
        if (
          previous?.countryCode &&
          place.countryCode &&
          previous.countryCode !== place.countryCode
        ) {
          setToPlace(null);
          setDestinationKey((key) => key + 1);
        }
        return place;
      });
    },
    [clearFareResult, clearValidationError]
  );

  const handleToPlaceSelect = useCallback(
    (place: DailyCommuteLocation) => {
      clearFareResult();
      clearValidationError();
      setToPlace(place);
    },
    [clearFareResult, clearValidationError]
  );

  const handleClearFrom = useCallback(() => {
    clearFareResult();
    clearValidationError();
    setFromPlace(null);
  }, [clearFareResult, clearValidationError]);

  const handleClearTo = useCallback(() => {
    clearFareResult();
    clearValidationError();
    setToPlace(null);
    setDestinationKey((key) => key + 1);
  }, [clearFareResult, clearValidationError]);

  const handleMakeChange = useCallback(
    (makeId: string) => {
      clearFareResult();
      clearValidationError();
      setSelectedMakeId(makeId);
      setSelectedModelId("");
      // A different vehicle has a different capacity, so ask the user to re-enter it.
      setAvailableSeats(null);
    },
    [clearFareResult, clearValidationError]
  );

  const handleModelChange = useCallback(
    (modelId: string) => {
      clearFareResult();
      clearValidationError();
      setSelectedModelId(modelId);
    },
    [clearFareResult, clearValidationError]
  );

  const handleFuelTypeChange = useCallback(
    (value: FuelType) => {
      clearFareResult();
      clearValidationError();
      setFuelType(value);
    },
    [clearFareResult, clearValidationError]
  );

  const decrementSeats = useCallback(() => {
    clearFareResult();
    clearValidationError();
    setSeats((count) => Math.max(MIN_SEATS, count - 1));
  }, [clearFareResult, clearValidationError]);

  const incrementSeats = useCallback(() => {
    clearFareResult();
    clearValidationError();
    setSeats((count) => Math.min(maxOfferingSeats(availableSeats), count + 1));
  }, [clearFareResult, clearValidationError, availableSeats]);

  const decrementAvailableSeats = useCallback(() => {
    clearFareResult();
    clearValidationError();
    setAvailableSeats((count) =>
      count == null ? null : Math.max(MIN_AVAILABLE_SEATS, count - 1)
    );
  }, [clearFareResult, clearValidationError]);

  const incrementAvailableSeats = useCallback(() => {
    clearFareResult();
    clearValidationError();
    setAvailableSeats((count) =>
      Math.min(MAX_AVAILABLE_SEATS, (count ?? MIN_AVAILABLE_SEATS - 1) + 1)
    );
  }, [clearFareResult, clearValidationError]);

  const handleSearchFare = useCallback(() => {
    if (!fromPlace?.address?.trim()) {
      setValidationError("Please select a starting location.");
      return;
    }
    if (!toPlace?.address?.trim()) {
      setValidationError("Please select a destination.");
      return;
    }
    if (!selectedMake) {
      setValidationError("Please select a vehicle make.");
      return;
    }
    if (!selectedModel) {
      setValidationError("Please select a vehicle model.");
      return;
    }
    if (availableSeats == null || availableSeats < MIN_AVAILABLE_SEATS) {
      setValidationError("Please enter the number of available seats in your vehicle.");
      return;
    }
    if (seats > maxOfferingSeats(availableSeats)) {
      setValidationError(
        "Offering seats can be at most one less than available seats (one seat is the driver's)."
      );
      return;
    }

    setValidationError(null);
    fareMutation.mutate(
      buildFareCalculationRequest({
        origin: fromPlace.address,
        destination: toPlace.address,
        availableSeats,
        carCompany: selectedMake.maNa,
        carModel: selectedModel.moNa,
        fuelType,
      })
    );
  }, [
    fromPlace,
    toPlace,
    selectedMake,
    selectedModel,
    availableSeats,
    seats,
    fuelType,
    fareMutation,
  ]);

  const fareExplanationData = useMemo(() => {
    if (!fareResult || !selectedMake || !selectedModel) return null;
    const { perSeatPricing } = fareResult.fareCalculation;
    return {
      selectedFare,
      suggestedFare: perSeatPricing.suggestedFare,
      minFare: perSeatPricing.minimumFare,
      maxFare: perSeatPricing.maximumFare,
      currency: perSeatPricing.currency,
      vehicleDetails: {
        make: selectedMake.maNa,
        model: selectedModel.moNa,
      },
    };
  }, [fareResult, selectedMake, selectedModel, selectedFare]);

  return {
    fromPlace,
    toPlace,
    destinationKey,
    selectedMakeId,
    selectedModelId,
    fuelType,
    seats,
    availableSeats,
    fareResult,
    selectedFare,
    setSelectedFare,
    showExplanation,
    setShowExplanation,
    validationError,
    isDetectingLocation,
    showFareResults,
    makes,
    makesLoading,
    makesError,
    models,
    modelsLoading,
    modelsError,
    selectedMake,
    selectedModel,
    fromFullAddress,
    toFullAddress,
    fareMutation,
    fareExplanationData,
    clearFareResult,
    clearValidationError,
    resetForm,
    handleFromPlaceSelect,
    handleToPlaceSelect,
    handleClearFrom,
    handleClearTo,
    handleMakeChange,
    handleModelChange,
    handleFuelTypeChange,
    decrementSeats,
    incrementSeats,
    decrementAvailableSeats,
    incrementAvailableSeats,
    handleSearchFare,
  };
}
